import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Menu, X, Globe, ChevronDown, Briefcase, Mail, Info, Calendar,
  Video, Mic, Type, Headphones, Users, Map, Globe2, Navigation
} from 'lucide-react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isMenuFullyOpen, setIsMenuFullyOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const { language, setLanguage, t } = useLanguage()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    if (isMenuOpen) {
      const timer = setTimeout(() => {
        setIsMenuFullyOpen(true)
      }, 300) // Wait for animation to complete
      return () => clearTimeout(timer)
    } else {
      setIsMenuFullyOpen(false)
    }
  }, [isMenuOpen])

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'es' : 'en')
  }

  const scrollToSection = (sectionId: string) => {
    // If we're not on the homepage, navigate there first
    if (location.pathname !== '/') {
      navigate('/')
      // Wait for navigation and DOM update, then scroll
      // Use a polling mechanism to wait for the element to appear
      let attempts = 0
      const maxAttempts = 20 // Wait up to 2 seconds (20 * 100ms)
      const checkForElement = setInterval(() => {
        attempts++
        const element = document.getElementById(sectionId)
        if (element) {
          clearInterval(checkForElement)
          // Small additional delay to ensure layout is complete
          setTimeout(() => {
            const headerHeight = 64 // h-16 = 64px
            const elementPosition = element.offsetTop - headerHeight
            window.scrollTo({
              top: elementPosition,
              behavior: 'smooth'
            })
          }, 50)
        } else if (attempts >= maxAttempts) {
          clearInterval(checkForElement)
          console.warn(`Section ${sectionId} not found after navigation`)
        }
      }, 100)
    } else {
      // We're already on homepage, just scroll
      const element = document.getElementById(sectionId)
      if (element) {
        const headerHeight = 64 // h-16 = 64px
        const elementPosition = element.offsetTop - headerHeight
        window.scrollTo({
          top: elementPosition,
          behavior: 'smooth'
        })
      }
    }
  }

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/90 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
      role="banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo Section */}
          <div className="flex items-center space-x-4">
            <Link to="/">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="flex items-center space-x-2 cursor-pointer"
              >
              <img
                src="/logo.png"
                alt="LogiCloud Logo"
                className="h-10 w-auto"
              />
              <div className="flex flex-col">
                <span 
                  className="text-sm font-bold text-primary-600"
                  style={{color:'#336699', marginTop:'10px', marginBottom:'0px', paddingTop:'0', paddingBottom:'0', lineHeight:'0.9'}}
                >.mx</span>
                {/* <a 
                  href="https://noozter.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs text-gray-600 hover:text-primary-600 transition-colors"
                >
                  @noozter.com
                </a> */}
              </div>
            </motion.div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6" role="navigation" aria-label="Main navigation">
            <button
              onClick={() => scrollToSection('about')}
              className="text-gray-700 hover:text-primary-600 transition-colors"
            >
              {t('nav.about')}
            </button>
            
            {/* Our Apps Dropdown */}
            <div className="relative group">
              <button className="text-gray-700 hover:text-primary-600 transition-colors flex items-center space-x-1">
                <span>{t('nav.applications.ourApps')}</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                <div className="py-4">
                  {/* Artificial Intelligence Section */}
                  <div className="px-4 pb-3">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="w-2 h-2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full"></div>
                      <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">{t('nav.ai.sectionHeader')}</h3>
                    </div>
                    <div className="space-y-1 ml-4">
                      <a
                        href="https://noozter.com/transcribe"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-3 py-2 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-purple-50 hover:to-blue-50 hover:text-primary-600 transition-all duration-200 rounded-lg flex items-center justify-between group"
                      >
                        <span className="flex items-center space-x-2">
                          <div className="w-1.5 h-1.5 bg-purple-400 rounded-full"></div>
                          <span>{t('nav.ai.videoTranscription')}</span>
                        </span>
                        <svg className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                      <a
                        href="https://noozter.com/live-transcription"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-3 py-2 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-purple-50 hover:to-blue-50 hover:text-primary-600 transition-all duration-200 rounded-lg group"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className="w-1.5 h-1.5 bg-purple-400 rounded-full"></div>
                            <div>
                              <div className="font-medium">{t('nav.ai.liveTranscription')}</div>
                              <div className="text-xs text-gray-500">{t('nav.ai.liveTranscriptionDesc')}</div>
                            </div>
                          </div>
                          <svg className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </div>
                      </a>
                      <a
                        href="https://noozter.com/text-to-speech"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-3 py-2 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-purple-50 hover:to-blue-50 hover:text-primary-600 transition-all duration-200 rounded-lg flex items-center justify-between group"
                      >
                        <span className="flex items-center space-x-2">
                          <div className="w-1.5 h-1.5 bg-purple-400 rounded-full"></div>
                          <span>{t('nav.ai.textToSpeech')}</span>
                        </span>
                        <svg className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                      <a
                        href="https://noozter.com/transcribe-audio-rec"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-3 py-2 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-purple-50 hover:to-blue-50 hover:text-primary-600 transition-all duration-200 rounded-lg flex items-center justify-between group"
                      >
                        <span className="flex items-center space-x-2">
                          <div className="w-1.5 h-1.5 bg-purple-400 rounded-full"></div>
                          <span>{t('nav.ai.audioToText')}</span>
                        </span>
                        <svg className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    </div>
                  </div>

                  {/* Separator */}
                  <div className="mx-4 border-t border-gray-200"></div>

                  {/* Geo Location Section */}
                  <div className="px-4 pt-3">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="w-2 h-2 bg-gradient-to-r from-green-500 to-teal-500 rounded-full"></div>
                      <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">{t('nav.geo.sectionHeader')}</h3>
                    </div>
                    <div className="space-y-1 ml-4">
                      <a
                        href="https://noozter.com/map"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-3 py-2 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-green-50 hover:to-teal-50 hover:text-primary-600 transition-all duration-200 rounded-lg flex items-center justify-between group"
                      >
                        <span className="flex items-center space-x-2">
                          <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                          <span>{t('nav.geo.yourMap')}</span>
                        </span>
                        <svg className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                      <a
                        href="https://noozter.com/world-nooz/mx"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-3 py-2 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-green-50 hover:to-teal-50 hover:text-primary-600 transition-all duration-200 rounded-lg flex items-center justify-between group"
                      >
                        <span className="flex items-center space-x-2">
                          <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                          <span>{t('nav.geo.countryMap')}</span>
                        </span>
                        <svg className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                      <a
                        href="https://noozter.com/world-nooz"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-3 py-2 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-green-50 hover:to-teal-50 hover:text-primary-600 transition-all duration-200 rounded-lg flex items-center justify-between group"
                      >
                        <span className="flex items-center space-x-2">
                          <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                          <span>{t('nav.geo.world')}</span>
                        </span>
                        <svg className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                      <a
                        href="https://noozter.com/track-map"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-3 py-2 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-green-50 hover:to-teal-50 hover:text-primary-600 transition-all duration-200 rounded-lg flex items-center justify-between group"
                      >
                        <span className="flex items-center space-x-2">
                          <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                          <span>{t('nav.geo.getDirections')}</span>
                        </span>
                        <svg className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    </div>
                  </div>
                  
                  {/* Separator */}
                  <div className="mx-4 border-t border-gray-200 my-3"></div>
                  
                  {/* Live Meeting Demo Button */}
                  <div className="px-4 pb-4">
                    <a
                      href="https://radio.logicloud.mx/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all text-center font-medium"
                    >
                      Live meeting demo ↗
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => scrollToSection('services')}
              className="text-gray-700 hover:text-primary-600 transition-colors"
            >
              {t('nav.services')}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-gray-700 hover:text-primary-600 transition-colors"
            >
              {t('nav.contact')}
            </button>
          </nav>

          {/* Desktop CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary-600 transition-colors"
            >
              <Globe className="h-4 w-4" />
              <span>{language.toUpperCase()}</span>
            </button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => scrollToSection('book-demo')}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <span>{t('nav.demo')}</span>
            </motion.button>
          </div>

          {/* Mobile menu button and language toggle */}
          <div className="lg:hidden flex items-center space-x-2">
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Globe className="h-4 w-4" />
              <span>{language.toUpperCase()}</span>
            </button>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-3 rounded-lg bg-primary-600 hover:bg-primary-700 text-white transition-colors shadow-md relative z-50"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <motion.div
          initial={false}
          animate={{ height: isMenuOpen ? 'auto' : 0, opacity: isMenuOpen ? 1 : 0 }}
          transition={{ duration: 0.3 }}
          className="lg:hidden overflow-hidden bg-white border-t border-gray-200 relative z-50 shadow-lg max-h-[85vh] overflow-y-auto"
        >
          <div className="py-2 space-y-0.5 relative z-50 bg-white">
            {/* Services - Moved to top */}
            <button
              onClick={() => {
                if (isMenuFullyOpen) {
                  setIsMenuOpen(false)
                  // Small delay to allow menu to close before scrolling
                  setTimeout(() => {
                    scrollToSection('services')
                  }, 100)
                }
              }}
              disabled={!isMenuFullyOpen}
              className={`flex items-center space-x-2 w-full text-left px-3 py-2 text-gray-700 hover:text-primary-600 hover:bg-gray-50 rounded-md transition-colors relative z-50 cursor-pointer ${
                !isMenuFullyOpen ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-md flex items-center justify-center">
                <Briefcase className="h-4 w-4 text-white" />
              </div>
              <span className="font-medium text-sm">{t('nav.services')}</span>
            </button>
            
            {/* Contact - Moved to top */}
            <button
              onClick={() => {
                if (isMenuFullyOpen) {
                  setIsMenuOpen(false)
                  // Small delay to allow menu to close before scrolling
                  setTimeout(() => {
                    scrollToSection('contact')
                  }, 100)
                }
              }}
              disabled={!isMenuFullyOpen}
              className={`flex items-center space-x-2 w-full text-left px-3 py-2 text-gray-700 hover:text-primary-600 hover:bg-gray-50 rounded-md transition-colors relative z-50 cursor-pointer ${
                !isMenuFullyOpen ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-green-500 to-teal-500 rounded-md flex items-center justify-center">
                <Mail className="h-4 w-4 text-white" />
              </div>
              <span className="font-medium text-sm">{t('nav.contact')}</span>
            </button>
            
            {/* About - Moved after Services and Contact */}
            <button
              onClick={() => {
                if (isMenuFullyOpen) {
                  setIsMenuOpen(false)
                  // Small delay to allow menu to close before scrolling
                  setTimeout(() => {
                    scrollToSection('about')
                  }, 100)
                }
              }}
              disabled={!isMenuFullyOpen}
              className={`flex items-center space-x-2 w-full text-left px-3 py-2 text-gray-700 hover:text-primary-600 hover:bg-gray-50 rounded-md transition-colors relative z-50 cursor-pointer ${
                !isMenuFullyOpen ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-md flex items-center justify-center">
                <Info className="h-4 w-4 text-white" />
              </div>
              <span className="font-medium text-sm">{t('nav.about')}</span>
            </button>
            
            {/* Our Apps Mobile Section */}
            <div className="px-4 py-3 bg-gray-50 border-l-4 border-primary-600">
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-2 h-2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full"></div>
                <div className="text-sm font-semibold text-gray-800 uppercase tracking-wide">{t('nav.applications.ourApps')}</div>
              </div>
              
              {/* Artificial Intelligence Mobile Section */}
              <div className="ml-4 mb-4">
                <div className="flex items-center space-x-2 mb-2">
                  <div className="w-1.5 h-1.5 bg-purple-400 rounded-full"></div>
                  <div className="text-xs font-medium text-gray-600 uppercase tracking-wide">{t('nav.ai.sectionHeader')}</div>
                </div>
                <div className="ml-4 space-y-1">
                  <a
                    href="https://noozter.com/transcribe"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-sm text-gray-600 hover:text-primary-600 transition-colors flex items-center justify-between py-1"
                  >
                    <span className="flex items-center space-x-2">
                      <Video className="w-4 h-4 text-purple-500" />
                      <span>{t('nav.ai.videoTranscription')}</span>
                    </span>
                    <svg className="w-3 h-3 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                  <a
                    href="https://noozter.com/live-transcription"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-sm text-gray-600 hover:text-primary-600 transition-colors py-1"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Mic className="w-4 h-4 text-purple-500 flex-shrink-0" />
                        <div>
                          <div className="font-medium">{t('nav.ai.liveTranscription')}</div>
                          <div className="text-xs text-gray-500">{t('nav.ai.liveTranscriptionDesc')}</div>
                        </div>
                      </div>
                      <svg className="w-3 h-3 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </div>
                  </a>
                  <a
                    href="https://noozter.com/text-to-speech"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-sm text-gray-600 hover:text-primary-600 transition-colors flex items-center justify-between py-1"
                  >
                    <span className="flex items-center space-x-2">
                      <Type className="w-4 h-4 text-purple-500" />
                      <span>{t('nav.ai.textToSpeech')}</span>
                    </span>
                    <svg className="w-3 h-3 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                  <a
                    href="https://noozter.com/transcribe-audio-rec"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-sm text-gray-600 hover:text-primary-600 transition-colors flex items-center justify-between py-1"
                  >
                    <span className="flex items-center space-x-2">
                      <Headphones className="w-4 h-4 text-purple-500" />
                      <span>{t('nav.ai.audioToText')}</span>
                    </span>
                    <svg className="w-3 h-3 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                  {/* Live Meeting Demo - Moved to AI Section */}
                  <a
                    href="https://radio.logicloud.mx/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-sm text-gray-600 hover:text-primary-600 transition-colors flex items-center justify-between py-1"
                  >
                    <span className="flex items-center space-x-2">
                      <Users className="w-4 h-4 text-purple-500" />
                      <span>{t('nav.ai.liveMeetingDemo')}</span>
                    </span>
                    <svg className="w-3 h-3 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>

              {/* Separator */}
              <div className="mx-4 border-t border-gray-200 my-3"></div>

              {/* Geo Location Mobile Section */}
              <div className="ml-4">
                <div className="flex items-center space-x-2 mb-2">
                  <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                  <div className="text-xs font-medium text-gray-600 uppercase tracking-wide">{t('nav.geo.sectionHeader')}</div>
                </div>
                <div className="ml-4 space-y-1">
                  <a
                    href="https://noozter.com/map"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-sm text-gray-600 hover:text-primary-600 transition-colors flex items-center justify-between py-1"
                  >
                    <span className="flex items-center space-x-2">
                      <Map className="w-4 h-4 text-green-500" />
                      <span>{t('nav.geo.yourMap')}</span>
                    </span>
                    <svg className="w-3 h-3 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                  <a
                    href="https://noozter.com/world-nooz/mx"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-sm text-gray-600 hover:text-primary-600 transition-colors flex items-center justify-between py-1"
                  >
                    <span className="flex items-center space-x-2">
                      <Globe className="w-4 h-4 text-green-500" />
                      <span>{t('nav.geo.countryMap')}</span>
                    </span>
                    <svg className="w-3 h-3 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                  <a
                    href="https://noozter.com/world-nooz"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-sm text-gray-600 hover:text-primary-600 transition-colors flex items-center justify-between py-1"
                  >
                    <span className="flex items-center space-x-2">
                      <Globe2 className="w-4 h-4 text-green-500" />
                      <span>{t('nav.geo.world')}</span>
                    </span>
                    <svg className="w-3 h-3 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                  <a
                    href="https://noozter.com/track-map"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-sm text-gray-600 hover:text-primary-600 transition-colors flex items-center justify-between py-1"
                  >
                    <span className="flex items-center space-x-2">
                      <Navigation className="w-4 h-4 text-green-500" />
                      <span>{t('nav.geo.getDirections')}</span>
                    </span>
                    <svg className="w-3 h-3 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Book Demo Button with Icon */}
            <div className="px-4 py-2 space-y-2">
              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  setTimeout(() => scrollToSection('book-demo'), 100);
                }}
                className="flex items-center justify-center space-x-2 w-full px-4 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-lg hover:from-primary-700 hover:to-purple-700 transition-all text-center font-medium shadow-md"
              >
                <Calendar className="h-5 w-5" />
                <span>{t('nav.demo')}</span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.header>
  )
}

export default Header
